import { useContext } from 'react';
import { useFormikContext } from 'formik';
import AttachmentsReview from 'src/components/Review/AttachmentsReview';
import OwnersStatement from 'src/components/Review/OwnersStatement';
import PropertyInfo from 'src/components/Review/PropertyInfo';
import PropertyOwnerInfo from 'src/components/Review/PropertyOwnerInfo';
import PropertyType from 'src/components/Review/PropertyType';
import ReviewSection from 'src/components/Review/ReviewSection';
import ValueTaxInfo from 'src/components/Review/ValueTaxInfo';
import AgreedToTermsCheckbox from 'src/components/util/AgreedToTermsCheckbox';
import Section from 'src/components/util/Section';
import FormContext from 'src/contexts/settlement-context';

/**

Review component displays all the information that the user has entered in the form, allowing the user to review and make sure everything is correct before submitting.
@component
@example
const formData = {
owner: {
name: 'John Smith',
email: 'john@smith.com',
},
type: 'residential',
info: {
address: '123 Main St',
city: 'New York',
state: 'NY',
zip: '10001',
},
valueAndTaxInfo: {
value: '$500,000',
taxAssessmentYear: '2020',
},
statement: 'I am the owner of the property and all the information is correct',
valuation: [
{
 name: 'valuation1.pdf',
 url: 'https://example.com/valuation1.pdf',
},
{
 name: 'valuation2.pdf',
 url: 'https://example.com/valuation2.pdf',
},
],
personal: [
{
 name: 'personal1.pdf',
 url: 'https://example.com/personal1.pdf',
},
],
other: [
{
 name: 'other1.pdf',
 url: 'https://example.com/other1.pdf',
},
],
}
return (
<Review formData={formData} />
)
*/
export default function Review() {
  const { formData, changeStep } = useContext(FormContext);
  const { errors } = useFormikContext();

  const sections = [
    {
      associatedStepNumber: 0,
      component: <PropertyOwnerInfo info={formData?.owner} />,
      title: 'Property Owner Information',
    },
    {
      associatedStepNumber: 1,
      component: <PropertyType type={formData?.type} />,
      title: 'Property Type',
    },
    {
      associatedStepNumber: 2,
      component: <PropertyInfo type={formData?.type} info={formData?.info} />,
      title: 'Property Information',
    },
    {
      associatedStepNumber: 3,
      component: <ValueTaxInfo type={formData?.type} info={formData?.valueAndTaxInfo} />,
      title: 'Value and Tax Information',
    },
    {
      associatedStepNumber: 4,
      component: <OwnersStatement info={formData?.statement} />,
      title: "Owner's Statement of Circumstances",
    },
    {
      associatedStepNumber: 5,
      component: (
        <AttachmentsReview
          attachments={{
            valuation: formData.valuation,
            personal: formData.personal,
            other: formData.other,
          }}
        />
      ),
      title: 'Attachments',
    },
    {
      component: (
        <AgreedToTermsCheckbox
          label="To the best of my knowledge and understanding, the information that I submitted is true, correct, and complete, and I understand that an incomplete application WILL NOT BE CONSIDERED."
          name="agreedToTerms"
          errorMessage={errors?.agreedToTerms}
        />
      ),
      edit: false,
      title: 'By submitting this document, I declare the following:',
    },
  ];

  return (
    <Section title="Review">
      {sections.map(({ associatedStepNumber, component, edit, title }, index) => (
        <ReviewSection
          associatedStepNumber={associatedStepNumber}
          changeStep={changeStep}
          edit={edit}
          key={index}
          title={title}
        >
          {component}
        </ReviewSection>
      ))}
    </Section>
  );
}
